// src/hooks/useAuth.js
// Firebase Authentication — keeps Firebase, adds phone+role to Firestore profile
import { useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

export function useAuth() {
  const [user,    setUser]    = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // ── Listen to auth state ──────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (snap.exists()) setProfile(snap.data());
      } else {
        setProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // ── Register — Name + Email + Phone + Password + Role ───
  const registerWithEmail = async (email, password, fullName, phone = '', role = 'talent') => {
    setError(null);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: fullName });

      const userData = {
        uid:           cred.user.uid,
        email,
        displayName:   fullName,
        fullName,
        phone,
        role,                        // 'talent' | 'collaborator' | 'admin'
        kycStatus:     'pending',
        walletAddress: null,
        ontBalance:    100,          // welcome bonus
        profileComplete: false,
        suspended:     false,
        createdAt:     serverTimestamp(),
        updatedAt:     serverTimestamp(),
      };

      await setDoc(doc(db, 'users', cred.user.uid), userData);
      setProfile(userData);
      return cred.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // ── Login ─────────────────────────────────
  const loginWithEmail = async (email, password) => {
    setError(null);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      // Refresh profile from Firestore
      const snap = await getDoc(doc(db, 'users', cred.user.uid));
      if (snap.exists()) setProfile(snap.data());
      return cred.user;
    } catch (err) {
      // Make Firebase errors human-readable
      const msg = err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password'
        ? 'Invalid email or password'
        : err.code === 'auth/user-not-found'
        ? 'No account found with this email'
        : err.code === 'auth/too-many-requests'
        ? 'Too many attempts. Please try again later.'
        : err.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  // ── Google Sign-In ─────────────────────────
  const loginWithGoogle = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      const ref  = doc(db, 'users', cred.user.uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        await setDoc(ref, {
          uid:           cred.user.uid,
          email:         cred.user.email,
          displayName:   cred.user.displayName,
          fullName:      cred.user.displayName,
          photoURL:      cred.user.photoURL,
          phone:         '',
          role:          'talent',
          kycStatus:     'pending',
          walletAddress: null,
          ontBalance:    100,
          profileComplete: false,
          suspended:     false,
          createdAt:     serverTimestamp(),
          updatedAt:     serverTimestamp(),
        });
      }
      const updated = await getDoc(ref);
      if (updated.exists()) setProfile(updated.data());
      return cred.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // ── Password Reset ─────────────────────────
  const resetPassword = async (email) => {
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // ── Sign Out ───────────────────────────────
  const logout = async () => {
    setError(null);
    try {
      await signOut(auth);
      setProfile(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    user,
    profile,
    loading,
    error,
    isAuthenticated: !!user,
    isCollaborator:  profile?.role === 'collaborator',
    isAdmin:         profile?.role === 'admin',
    registerWithEmail,
    loginWithEmail,
    loginWithGoogle,
    resetPassword,
    logout,
  };
}
